import {
	Box,
	Button,
	Dialog,
	DialogContent,
	DialogTitle,
	Typography,
	useTheme,
	useMediaQuery,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";

import { purple, grey } from "@mui/material/colors";
import { useEffect, useState } from "react";
import IconSwitch from "./iconSwitch";

const MealRow = ({
	label,
	count,
	disabled,
	onDecrement,
	onIncrement,
	randomize,
}) => (
	<Box
		sx={{
			display: "flex",
			justifyContent: "space-between",
			alignItems: "center",
			width: "90%",
			//padding: 1,
			//gap: 2,
		}}
	>
		<Typography sx={{ width: "100px", textAlign: "center", flexShrink: 0 }}>
			{label}
		</Typography>

		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 2,
			}}
		>
			<Button disabled={count <= 0 || randomize} onClick={onDecrement}>
				-
			</Button>
			<Typography
				sx={{
					textAlign: "center",
					minWidth: 40,
					color: randomize ? grey[400] : grey[800],
				}}
			>
				{randomize ? "?" : count}
			</Typography>
			<Button disabled={disabled || randomize} onClick={onIncrement}>
				+
			</Button>
		</Box>
	</Box>
);

function GenerateMealPlanDialog({ openStatus, onClose, onMealPlanCreated }) {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const [randomize, setRandomize] = useState(false);
	const [meatCount, setMeatCount] = useState(1);
	const [fishCount, setFishCount] = useState(1);
	const [chickenCount, setChickenCount] = useState(1);
	const [vegCount, setVegCount] = useState(1);
	const [buttonDisabled, setDisabled] = useState(false);
	const [numberButtonDisabled, setNumberDisabled] = useState(false);
	const [totalCount, setTotalCount] = useState();
	const [loading, setLoading] = useState(false);
	const [maxCount, setMaxCount] = useState(7);

	//const numberOfWeekdays = 7;
	//var currentCount = 4;

	useEffect(() => {
		const total = meatCount + fishCount + chickenCount + vegCount;
		setDisabled(total >= maxCount);
		setNumberDisabled(maxCount >= 7);
		setTotalCount(total);
		if (totalCount > maxCount) {
			setVegCount(0);
			setMeatCount(0);
			setFishCount(0);
			setChickenCount(0);
		}
	}, [meatCount, fishCount, chickenCount, vegCount, maxCount, totalCount]);

	function handleButtonClick(mealType, value) {
		switch (mealType) {
			case "Meat":
				setMeatCount((prev) => Math.max(0, prev + value));
				break;
			case "Fish":
				setFishCount((prev) => Math.max(0, prev + value));
				break;
			case "Chicken":
				setChickenCount((prev) => Math.max(0, prev + value));
				break;
			case "Veg":
				setVegCount((prev) => Math.max(0, prev + value));
				break;
			case "MaxCount":
				setMaxCount((prev) => Math.max(0, prev + value));
				break;
			default:
				console.log("There was an error updating the dish count.");
		}
	}
	function toggleRandomize() {
		setRandomize(!randomize);
	}

	const generateFilteredWeek = async (
		chickenCount = 0,
		meatCount = 0,
		fishCount = 0,
		vegCount = 0,
		maxCount = 7
	) => {
		setLoading(true);
		const payload = {
			chickenCount,
			meatCount,
			fishCount,
			vegCount,
			maxCount,
		};
		try {
			const res = await fetch(
				"https://us-central1-mealplanner-f6b88.cloudfunctions.net/api/full-week-with-filters",
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
					},
					body: JSON.stringify(payload),
				}
			);
			const data = await res.json();
			onMealPlanCreated(data);
			setLoading(false);
		} catch (err) {
			console.log(err);
		} finally {
			setLoading(false);
			onClose();
		}
	};
	return (
		<Dialog
			open={openStatus}
			onClose={onClose}
			aria-labelledby="create-dialog-title"
			aria-describedby="create-dialog-description"
			fullWidth={!isMobile}
			maxWidth="md"
			PaperProps={{
				sx: {
					//height: "70vh", // or any custom height like 400, '50%', etc.
					maxHeight: "90vh", // optional: limit max height
				},
			}}
		>
			<DialogTitle
				variant="h2"
				sx={{
					fontSize: { xs: "1.5rem", md: "2.5rem" },
					fontWeight: "bold",
					color: "#333",
					mb: 2,
				}}
				id="create-dialog-title"
			>
				{"Create New Meal Plan"}
			</DialogTitle>
			<DialogContent>
				<Box
					display="flex"
					flexDirection={{ xs: "column", md: "row" }}
					gap={2} // adds spacing between boxes
				>
					<Box
						sx={{
							width: "100%",
							marginLeft: isMobile ? 0 : 6,
							//backgroundColor: purple[200],
						}}
					>
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								justifyContent: "center",
								alignItems: "center",
								textAlign: "center",
								gap: 0,
								paddingBottom: 5,
							}}
						>
							<IconSwitch
								sx={{ color: purple[300] }}
								checked={randomize}
								onChange={toggleRandomize}
							/>
							Randomize Meals
							<Box sx={{ gap: 0, paddingTop: 5 }}>
								<Typography variant="overline">Number of meals</Typography>
								<Box
									sx={{
										display: "flex",
										flexDirection: "row",
										justifyContent: "center",
										alignItems: "center",
										gap: 5,
									}}
								>
									<Button
										disabled={maxCount === 1}
										onClick={() => {
											handleButtonClick("MaxCount", -1);
										}}
										size="small"
										variant="contained"
									>
										-
									</Button>
									<Typography>{maxCount}</Typography>
									<Button
										disabled={numberButtonDisabled}
										onClick={() => {
											handleButtonClick("MaxCount", 1);
										}}
										size="small"
										variant="contained"
									>
										+
									</Button>
								</Box>
							</Box>
						</Box>

						<Typography
							variant="overline"
							sx={{ display: "block", textAlign: "center", width: "100%" }}
						>
							Or select your distribution
						</Typography>
						<Box
							sx={{
								display: "flex",
								flexDirection: "column", // mobile default
								gap: 1,
								width: "90%",
								"@media (min-width:600px)": {
									width: "80%",
									margin: "0  auto",
								},
							}}
						>
							<MealRow
								label="Meat"
								count={meatCount}
								disabled={buttonDisabled}
								randomize={randomize}
								onDecrement={() => handleButtonClick("Meat", -1)}
								onIncrement={() => handleButtonClick("Meat", 1)}
							/>
							<MealRow
								label="Fish"
								count={fishCount}
								disabled={buttonDisabled}
								randomize={randomize}
								onDecrement={() => handleButtonClick("Fish", -1)}
								onIncrement={() => handleButtonClick("Fish", 1)}
							/>
							<MealRow
								label="Chicken"
								count={chickenCount}
								disabled={buttonDisabled}
								randomize={randomize}
								onDecrement={() => handleButtonClick("Chicken", -1)}
								onIncrement={() => handleButtonClick("Chicken", 1)}
							/>
							<MealRow
								label="Vegetarian"
								count={vegCount}
								disabled={buttonDisabled}
								randomize={randomize}
								onDecrement={() => handleButtonClick("Veg", -1)}
								onIncrement={() => handleButtonClick("Veg", 1)}
							/>
						</Box>
					</Box>
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							justifyContent: "center",
							alignItems: "center",
							//backgroundColor: purple[200],
							width: "100%",
						}}
					>
						<Box height="20px" />
						<Typography
							width="100%"
							hidden={!randomize}
							textAlign="center"
							variant="overline"
							sx={{ lineHeight: 1.5 }}
						>
							You've decided to randomize your meal plan. {maxCount} meals will be
							chosen for you.
						</Typography>
						<Typography
							width="100%"
							hidden={randomize}
							variant="overline"
							textAlign="center"
							sx={{ textAlign: "center", lineHeight: 1.5 }}
						>
							You've picked {totalCount} meals for your plan.
							{totalCount >= maxCount
								? ""
								: " The remaining " + (maxCount - totalCount) + " will be randomized."}
						</Typography>
						<Box sx={{ p: 1 }} />
						<LoadingButton
							variant="contained"
							loading={loading}
							loadingIndicator={"Loading..."}
							onClick={() =>
								generateFilteredWeek(
									chickenCount,
									meatCount,
									fishCount,
									vegCount,
									maxCount
								)
							}
						>
							Create
						</LoadingButton>
					</Box>
				</Box>
				<Box sx={{ p: isMobile ? 1 : 3 }} />
			</DialogContent>
		</Dialog>
	);
}
export default GenerateMealPlanDialog;
